import React, { useState, useEffect } from "react";
import { Booking } from "../../types/types";
import PrimaryButton from "../../components/common/buttons/PrimaryButton";
import {
  X,
  Check,
  Globe,
  Loader2,
  Briefcase,
  Tag,
  LayoutList,
} from "lucide-react";
import {
  getBookingById,
  getSupabaseDropdownOptions,
} from "./bookingController";
import LoadingIndicator from "../../components/common/loadingIndicator/LoadingIndicator";

interface CompleteBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (details: CompletionDetails) => Promise<void>;
  booking: Booking | null;
}

export interface CompletionDetails {
  agentName: string | null;
  ticketSellingPrice: number;
  currency: string;
  platform: string;
  issuedFrom: string;
  agentCategory: string | null;
  adultSellingPrice: number;
  childSellingPrice: number;
  infantSellingPrice: number;
  bookingSource: string; // ✅ NEW: Sent to the backend
}

const CompleteBookingModal: React.FC<CompleteBookingModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  booking,
}) => {
  const [currency, setCurrency] = useState("LKR");
  const [platform, setPlatform] = useState("");
  const [issuedFrom, setIssuedFrom] = useState("");
  const [agent, setAgent] = useState("");

  // ✅ NEW: Source state with default value
  const [bookingSource, setBookingSource] = useState("FINDYOURFARES");

  const [adultPrice, setAdultPrice] = useState<string>("");
  const [childPrice, setChildPrice] = useState<string>("");
  const [infantPrice, setInfantPrice] = useState<string>("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingLocal, setIsLoadingLocal] = useState(false);
  const [localBookingData, setLocalBookingData] = useState<any>(null);

  const [agentOptions, setAgentOptions] = useState<string[]>([]);
  const [platformOptions, setPlatformOptions] = useState<string[]>([]);
  const [issuedFromOptions, setIssuedFromOptions] = useState<string[]>([]);

  const paxList = booking?.bookingDetails?.passengers || [];
  const adultCount = paxList.filter((p) =>
    ["ADT", "ADULT"].includes((p.type || "").toUpperCase()),
  ).length;
  const childCount = paxList.filter((p) =>
    ["CHD", "CHILD", "CH"].includes((p.type || "").toUpperCase()),
  ).length;
  const infantCount = paxList.filter((p) =>
    ["INF", "INFANT"].includes((p.type || "").toUpperCase()),
  ).length;

  const fetchLocalDataAndOptions = async (currentCurrency: string) => {
    if (!booking?.bookingId) return;
    setIsLoadingLocal(true);
    try {
      const [data, dropdownOptions] = await Promise.all([
        getBookingById(booking.bookingId),
        // ✅ NEW: Pass the currency to the API call!
        getSupabaseDropdownOptions(currentCurrency),
      ]);

      setLocalBookingData(data);
      setAgentOptions(dropdownOptions.agents || []);
      setPlatformOptions(dropdownOptions.platforms || []);
      setIssuedFromOptions(dropdownOptions.issuedFrom || []);
    } catch (error) {
      console.error("Failed to fetch modal data", error);
    } finally {
      setIsLoadingLocal(false);
    }
  };

  useEffect(() => {
    if (isOpen && booking) {
      // ✅ NEW: Determine the currency immediately
      let initialCurrency = "LKR";
      if (booking.bookingDetails?.paymentInfo?.currency) {
        initialCurrency = booking.bookingDetails.paymentInfo.currency;
        setCurrency(initialCurrency);
      }

      setLocalBookingData(null);
      setAdultPrice("");
      setChildPrice("");
      setInfantPrice("");
      setAgent("");
      setPlatform("");
      setIssuedFrom("");
      setBookingSource("FINDYOURFARES");

      if (booking.bookingId) {
        // ✅ NEW: Pass the determined currency into the fetcher
        fetchLocalDataAndOptions(initialCurrency);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, booking]);

  useEffect(() => {
    if (isOpen && localBookingData) {
      if (localBookingData.platform) setPlatform(localBookingData.platform);
      if (localBookingData.issuedFrom)
        setIssuedFrom(localBookingData.issuedFrom);
      if (localBookingData.agent) setAgent(localBookingData.agent);
      if (localBookingData.currency) setCurrency(localBookingData.currency);
    }
  }, [isOpen, localBookingData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!platform || !issuedFrom) {
      alert("Please select Platform and Issued From.");
      return;
    }

    // ✅ NEW: Validate Agent only if Source is "Agent"
    if (bookingSource === "AGENT" && !agent) {
      alert("Please select an Agent.");
      return;
    }

    const aPrice = parseFloat(adultPrice || "0");
    const cPrice = parseFloat(childPrice || "0");
    const iPrice = parseFloat(infantPrice || "0");
    const totalSellingPrice =
      aPrice * adultCount + cPrice * childCount + iPrice * infantCount;

    if (totalSellingPrice <= 0) {
      alert("Please enter a valid price for the passengers.");
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm({
        // ✅ NEW: Nullify agent info if the source is not "Agent"
        agentName: bookingSource === "AGENT" ? agent : null,
        agentCategory: bookingSource === "AGENT" ? agent : null,
        bookingSource: bookingSource,

        ticketSellingPrice: totalSellingPrice,
        adultSellingPrice: aPrice,
        childSellingPrice: cPrice,
        infantSellingPrice: iPrice,
        currency,
        platform,
        issuedFrom,
      });
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !booking) return null;

  const currentTotalAmount = Number(
    booking.bookingDetails?.paymentInfo?.totalAmount || 0,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 transform transition-all">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">Complete Booking</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
        </div>

        {isLoadingLocal && (
          <div className="mb-4 flex items-center gap-2 text-sm text-indigo-600 bg-indigo-50 p-3 rounded">
            <LoadingIndicator /> Loading details & databases...
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* ✅ NEW: Booking Source Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Source
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LayoutList size={18} className="text-gray-400" />
              </div>
              <select
                value={bookingSource}
                onChange={(e) => setBookingSource(e.target.value)}
                className="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                disabled={isSubmitting || isLoadingLocal}
              >
                <option value="FINDYOURFARES">FindyourFares</option>
                <option value="WALK_IN">Walk-in</option>
                <option value="AGENT">Agent</option>
              </select>
            </div>
          </div>

          {/* ✅ DYNAMIC AGENT: Only visible if "Agent" is selected above */}
          {bookingSource === "AGENT" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agent
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Tag size={18} className="text-gray-400" />
                </div>
                <select
                  value={agent}
                  onChange={(e) => setAgent(e.target.value)}
                  className="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  disabled={isSubmitting || isLoadingLocal}
                >
                  <option value="">Select Agent</option>
                  {agentOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Individual Prices */}
          <div>
            <div className="flex justify-between items-end mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Cost Price (Per Person)
              </label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-20 p-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50 text-gray-700 text-xs font-medium"
                disabled={isSubmitting || isLoadingLocal}
              >
                <option value="LKR">LKR</option>
                <option value="EUR">EUR</option>
              </select>
            </div>

            <div className="space-y-2">
              {adultCount > 0 && (
                <div className="flex items-center gap-2">
                  <span className="w-16 text-sm text-gray-600">Adult(s)</span>
                  <span className="text-xs text-gray-400">x{adultCount}</span>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={adultPrice}
                    onChange={(e) => setAdultPrice(e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Price per Adult"
                    disabled={isSubmitting || isLoadingLocal}
                  />
                </div>
              )}
              {childCount > 0 && (
                <div className="flex items-center gap-2">
                  <span className="w-16 text-sm text-gray-600">Child</span>
                  <span className="text-xs text-gray-400">x{childCount}</span>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={childPrice}
                    onChange={(e) => setChildPrice(e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Price per Child"
                    disabled={isSubmitting || isLoadingLocal}
                  />
                </div>
              )}
              {infantCount > 0 && (
                <div className="flex items-center gap-2">
                  <span className="w-16 text-sm text-gray-600">Infant</span>
                  <span className="text-xs text-gray-400">x{infantCount}</span>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={infantPrice}
                    onChange={(e) => setInfantPrice(e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Price per Infant"
                    disabled={isSubmitting || isLoadingLocal}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Dynamic Platform */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Platform
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Globe size={18} className="text-gray-400" />
              </div>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                disabled={isSubmitting || isLoadingLocal}
              >
                <option value="">Select Platform</option>
                {platformOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Dynamic Issued From */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Issued From
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Briefcase size={18} className="text-gray-400" />
              </div>
              <select
                value={issuedFrom}
                onChange={(e) => setIssuedFrom(e.target.value)}
                className="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                disabled={isSubmitting || isLoadingLocal}
              >
                <option value="">Select Partner</option>
                {issuedFromOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="pt-4 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <PrimaryButton
              className="flex-1 flex items-center justify-center"
              type="submit"
              disabled={isSubmitting || isLoadingLocal}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="mr-2 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Check size={18} className="mr-2" /> Done
                </>
              )}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompleteBookingModal;
